import { useCallback, useEffect, useRef, useState } from "react";

import { useWfAuthn } from "./useWfAuthn";

/**
 * The state of the step-up token's refresh token.
 *
 * The step-up token's validity is independent of the refresh token's validity.
 * As in, the refresh token can be expired while the step-up token is still
 * perfectly valid and usable for requests. Similarly, a valid refresh token
 * can be exchanged for a new step-up token, even if the current step-up token
 * is expired.
 */
export enum StepUpRefreshableState {
  /**
   * The refresh token is valid and can be used to refresh the step-up token.
   */
  VALID,

  /**
   * The refresh token is nearing idle state and will expire soon if not used.
   */
  NEARING_IDLE,

  /**
   * The refresh token is about to expire completely.
   */
  NEARING_EXPIRED,

  /**
   * The refresh token has expired and can no longer be used to refresh the
   * step-up token.
   */
  EXPIRED,

  /**
   * The step-up token is currently being fetched.
   */
  FETCHING,
}

export interface WfAuthnIdle {
  /**
   * The state of the step-up token's refresh token.
   */
  state: StepUpRefreshableState | null;
}

/**
 * A hook that tracks the state of the step-up token's refresh token.
 */
export function useWfAuthnIdle(
  props: {
    /**
     * The grace period in milliseconds before the refresh token is considered
     * nearing a particular expiry state. Defaults to 5 minutes.
     */
    gracePeriodMs?: number;
  } = {}
): WfAuthnIdle {
  const { gracePeriodMs = 1000 * 60 * 5 } = props;
  if (gracePeriodMs <= 0) {
    throw new Error("Grace period must be greater than 0");
  }

  const { currentStepUpToken, getStepUpToken } = useWfAuthn();
  const [state, setState] = useState<StepUpRefreshableState | null>(null);
  const [isTokenRetrieved, setIsTokenRetrieved] = useState<boolean>(false);
  const [fatal, setFatal] = useState<unknown>(undefined);

  // eslint-disable-next-line react-hooks/purity -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  const [lastUpdate, setLastUpdate] = useState<number>(Date.now());
  const timerId = useRef<number | null>(null);

  const handleTimer = useCallback(() => {
    setLastUpdate(Date.now());
  }, [setLastUpdate]);

  useEffect(() => {
    const retrieveToken = async () => {
      try {
        await getStepUpToken();
      } catch (error) {
        setFatal(error ?? new Error("Step-up token fetch failed"));
        return;
      }
      setIsTokenRetrieved(true);
    };

    if (currentStepUpToken === undefined) {
      if (isTokenRetrieved) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
        setState(StepUpRefreshableState.EXPIRED);
      } else {
        retrieveToken();
        setState(StepUpRefreshableState.FETCHING);
      }
      return;
    }

    if (currentStepUpToken.refresh === undefined) {
      throw new Error("Step up token expected to have refresh details");
    }

    if (currentStepUpToken.refresh.expired || currentStepUpToken.refresh.expiredIdle) {
      setState(StepUpRefreshableState.EXPIRED);
      return;
    }

    const expiresIn = currentStepUpToken.refresh.expiresIn;
    const nearExpiredIn = expiresIn - gracePeriodMs;

    const expiresIdleIn = currentStepUpToken.refresh.expiresIdleIn;
    const nearExpiredIdleIn = expiresIdleIn - gracePeriodMs;

    let nextTimer: number;
    if (nearExpiredIn <= 0) {
      setState(StepUpRefreshableState.NEARING_EXPIRED);
      nextTimer = expiresIn;
    } else if (nearExpiredIdleIn <= 0) {
      setState(StepUpRefreshableState.NEARING_IDLE);
      nextTimer = expiresIdleIn;
    } else {
      nextTimer = Math.min(nearExpiredIdleIn, nearExpiredIn);
      setState(StepUpRefreshableState.VALID);
    }

    timerId.current = window.setTimeout(handleTimer, nextTimer);
    return () => {
      if (timerId.current) {
        window.clearTimeout(timerId.current);
        timerId.current = null;
      }
    };
  }, [currentStepUpToken, gracePeriodMs, handleTimer, lastUpdate, setState, isTokenRetrieved, getStepUpToken]);

  if (fatal !== undefined) {
    throw fatal;
  }

  return {
    state,
  };
}
